import { defineComponent as _defineComponent } from 'vue'
import bugsnagClient from '../../global/bugsnag';
import ButtonSpinner from 'JS/components/ButtonSpinner.vue';
import CarouselArrowButton from 'JS/components/carousels/CarouselArrowButton.vue';
import CarouselTrackingService from 'JS/services/CarouselTrackingService';
import Price from 'JS/components/price/Price.vue';
import ProductService from "JS/services/ProductService";
import ReviewStars from '../../components/ReviewStars.vue';
import { CarouselType, ICarouselClickData, ICarouselClickGtmData, ICarouselViewData, ICarouselViewGtmData, IProductCarouselGtmProduct } from 'JS/types/Carousel';
import { computed, inject, nextTick, onMounted, PropType, ref, watch } from 'vue';
import { getPriceMode, IListableProductJson, IProductGroupSelectorValue } from "JS/types/Product";
import { handleTrackingLinkClick } from 'JS/utilities/carousel-tracking';
import { IPdpSimilarProduct } from 'JS/types/PDP';
import { PDPRootState } from 'JS/pdp/state/types';
import { useLegacyVuexStore } from 'JS/composables/legacy-vuex-store';
import { useListSlider } from 'JS/composables/list-slider';
import { useViewportWidth } from 'JS/composables/breakpoint';


export default /*#__PURE__*/_defineComponent({
  __name: 'CompareSimilarProducts',
  props: {
	currentProduct: {
		type: Object as PropType<IListableProductJson>,
		required: true
	}
},
  emits: ["add-to-cart"],
  setup(__props, { emit }: { emit: ({
	(event: 'add-to-cart'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props





const store = useLegacyVuexStore<PDPRootState>();
const products = ref<IPdpSimilarProduct[]>([]);
const mobileScrollWrapper = ref<HTMLElement | null>();

const productService = inject<ProductService>('productService');
const trackingService = new CarouselTrackingService();

const maxSwatches = 4;
let viewImpressionUrl = null;
let carouselView: ICarouselViewData | null = null;

const loading = ref(true);

const displayProducts = computed(()=> ({
	first: products.value[0],
	comparisons: products.value.slice(1, products.value.length),
}));

//SLIDER SECTION

const numSlides = ref(0);
const currentSlide = ref(0);

const { slider, viewport, previous, next } = useListSlider(() => {
	currentSlide.value = slider?.value.getCurrentSlide() ?? 0;
});

const viewportWidth = useViewportWidth();
watch(() => [viewportWidth.value, displayProducts.value.comparisons], () => {
	setupCarousel();
});

function setupCarousel() {
	// In order to get accurate values we need to wait until next tick
	nextTick(() => {
		slider.value?.reset();
		numSlides.value = slider.value?.getNumSlides();
		currentSlide.value = 0;
		mobileScrollWrapper.value.scrollLeft = 0;
	});
}

//END SLIDER SECTION

const totalSwatches = computed<IProductGroupSelectorValue[][]>(() => {
	if (products.value.length === 0) return [];

	const swatches: any[] = [];

	products.value.forEach(product => {
		if (!product.swatches) {
			swatches.push([]);
		} else {
			swatches.push(product.swatches.values);
		}
	});

	return swatches;
});

const displaySwatches = computed<IProductGroupSelectorValue[][]>(() => {
	if (products.value.length === 0) return [];

	const swatches: any[] = [];

	products.value.forEach(product => {
		if (!product.swatches) {
			swatches.push([]);
		} else {
			const coloredSwatches = product.swatches.values.filter(p => p.swatchUrl);
			swatches.push(coloredSwatches.slice(0, maxSwatches));
		}
	});

	return swatches;
});

const showRatingColumn = computed<boolean>(()=> {
	return !!products.value.find(p => p.avgRating);
});

const showSwatchColumn = computed<boolean>(()=> {
	return !!products.value.find(p => p.swatches);
});

const showAddToCartButton = computed<boolean>(() => {
	if (props.currentProduct.price.price.includes(' - ')) return false;

	if (store.state.disableAddToCartButton) {
		return false;
	}

	if (store.getters.isShippingDisabled || store.state.allAttributes.length > 0) {
		return false;
	}
	return true;
});

const addingToCart = computed(() => {
	return store.state.addingToCart;
});

function trackClick(e: MouseEvent, product: IPdpSimilarProduct, index: number) {
	const clickData: ICarouselClickData = {
		blainNumber: product.blainNumber,
		currentPrice: product.price.currentPrice,
		carousel: carouselView,
		itemIndex: index,
	};

	const gtmData: ICarouselClickGtmData = {
		itemListID: `carousel_${carouselView.type.toString()}`,
		itemListName: carouselView.carouselName,
		currentPrice: product.price.currentPrice,
		belowMap: product.price.isBelowMAP,
		priceMode: getPriceMode(product.price),
		blainNumber: product.blainNumber,
		vendorBrand: product.brand,
		productName: product.name,
		index: index
	};

	handleTrackingLinkClick(e, () => trackingService.trackClick(clickData, gtmData));
}

async function trackView() {
	if (viewImpressionUrl) return;

	viewImpressionUrl = window.location.pathname + window.location.search;

	carouselView = {
		type: CarouselType.SIMILAR_PRODUCTS,
		carouselName: "Compare Similar",
		url: viewImpressionUrl
	};

	const carouselProducts: IProductCarouselGtmProduct[] = [];
	products.value.forEach(product => {
		carouselProducts.push({
			blainNumber: product.blainNumber,
			vendorBrand: product.brand,
			productName: product.name,
			price: product.price,
			belowMap: product.price.isBelowMAP,
			priceMode: getPriceMode(product.price),
		});
	});

	const gtmData: ICarouselViewGtmData = {
		itemListID: `carousel_${carouselView.type.toString()}`,
		itemListName: carouselView.carouselName,
		carouselProducts
	};

	await trackingService.trackViews([gtmData]);

	// Also sent to GTM
	window.blainGtmDataLayer.push({
		'event': "compare_products"
	});
}


onMounted(async () => {
	await loadCarouselData();
	await trackView();
	setupCarousel();
});

async function loadCarouselData() {
	loading.value = true;

	try {
		const result: IPdpSimilarProduct[] = await productService.getSimilarProducts(props.currentProduct.blainNumber);
		if (!result) return;

		products.value = result;
	} catch (err) {
		bugsnagClient.notify(err);
		// eslint-disable-next-line no-console
		console.warn(err);
	}

	loading.value = false;
}

watch(() => props.currentProduct, () => {
	loadCarouselData();
});


return { __sfc: true,props, emit, store, products, mobileScrollWrapper, productService, trackingService, maxSwatches, viewImpressionUrl, carouselView, loading, displayProducts, numSlides, currentSlide, slider, viewport, previous, next, viewportWidth, setupCarousel, totalSwatches, displaySwatches, showRatingColumn, showSwatchColumn, showAddToCartButton, addingToCart, trackClick, trackView, loadCarouselData, ButtonSpinner, CarouselArrowButton, Price, ReviewStars }
}

})